import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import ReviewStar from "@redotech/redo-icons/review-star.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import PaperAirplane from "@redotech/redo-web/icon-old/paper-airplane.svg";
import { TextInput } from "@redotech/redo-web/text-input";
import classNames from "classnames";
import { FormEventHandler, memo, useEffect, useState } from "react";
import * as css from "./escalation-csat-form.module.css";

/** How long the user can revise their star rating after the first click. */
const EDITING_WINDOW_MS = 5 * 60 * 1000;

export const EscalationCsatForm = memo(function EscalationCsatForm({
  escalationId,
}: {
  escalationId: string;
}) {
  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const [hover, setHover] = useState(0);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [submittedComment, setSubmittedComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingDisabled, setEditingDisabled] = useState(false);
  const [editingExpiresAt, setEditingExpiresAt] = useState<Date | null>(null);

  // Check eligibility and load existing CSAT data if present
  const eligibilityLoad = useLoad(async () => {
    const result = await merchantRpcClient.shouldShowEscalationCsat({
      escalationId,
    });
    return result;
  }, [merchantRpcClient, escalationId]);

  // Initialize state from existing CSAT data when loading a previous thread
  useEffect(() => {
    if (eligibilityLoad.value?.existingRating) {
      setRating(eligibilityLoad.value.existingRating);
      setSubmitted(true);
      if (eligibilityLoad.value.existingComment) {
        setSubmittedComment(eligibilityLoad.value.existingComment);
      }
    }
  }, [eligibilityLoad.value]);

  const ratingLocked = submitting || submitted || editingDisabled;

  const submitRating = useHandler(async (selectedRating: number) => {
    if (
      submitting ||
      submitted ||
      (editingExpiresAt && new Date() > editingExpiresAt) ||
      editingDisabled
    ) {
      setEditingDisabled(true);
      return;
    }
    setSubmitting(true);
    setRating(selectedRating);
    try {
      await merchantRpcClient.submitEscalationCsat({
        escalationId,
        rating: selectedRating,
      });
      const expiresAt = new Date();
      expiresAt.setTime(expiresAt.getTime() + EDITING_WINDOW_MS);
      setEditingExpiresAt(expiresAt);
    } catch {
      // Rating submission failed silently — don't block the UI
    } finally {
      setSubmitting(false);
    }
  });

  const handleCommentSubmit: FormEventHandler = useHandler(async (e) => {
    e.preventDefault();
    if (!comment.trim() || !rating || submitting) {
      return;
    }
    setSubmitting(true);
    setEditingExpiresAt(new Date());
    try {
      await merchantRpcClient.submitEscalationCsat({
        escalationId,
        rating,
        comment: comment.trim(),
      });
      setSubmittedComment(comment.trim());
      setSubmitted(true);
    } catch {
      // Comment submission failed silently
    } finally {
      setSubmitting(false);
    }
  });

  // Don't render until eligibility is checked, or if not eligible
  if (!eligibilityLoad.value?.show) {
    return null;
  }

  return (
    <div className={css.csatCard}>
      <h3 className={css.cardTitle}>How was your support experience?</h3>

      <Flex className={css.starsContainer} justify="center" w="full">
        {[1, 2, 3, 4, 5].map((star) => (
          <div className={css.starWrapper} key={star}>
            <ReviewStar
              className={classNames(
                star <= rating || star <= hover
                  ? css.starFilled
                  : css.starEmpty,
                ratingLocked && css.starDisabled,
              )}
              onClick={() => submitRating(star)}
              onMouseLeave={() => !ratingLocked && setHover(rating)}
              onMouseOver={() => !ratingLocked && setHover(star)}
            />
          </div>
        ))}
      </Flex>

      {rating > 0 && (
        <div className={css.feedbackSection}>
          <p className={css.thankYouText}>Thank you for your feedback!</p>

          {!submittedComment && !submitted && (
            <>
              <p className={css.commentPrompt}>
                Want to share more about your experience?
              </p>
              <form className={css.commentForm} onSubmit={handleCommentSubmit}>
                <div className={css.commentInput}>
                  <TextInput
                    onChange={setComment}
                    placeholder="Optional feedback..."
                    value={comment}
                  />
                </div>
                <RedoButton
                  disabled={!comment.trim() || submitting}
                  hierarchy="secondary"
                  IconLeading={PaperAirplane}
                  size="xs"
                  text=""
                  type="submit"
                />
              </form>
            </>
          )}

          {submittedComment && (
            <div className={css.submittedComment}>
              <p className={css.commentLabel}>Your comment:</p>
              <div className={css.commentBubble}>{submittedComment}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});
